/** Presentational pieces for the Broker page (extracted from
 * BrokerConnectPage to keep each file/function within the lint limits).
 * All are pure — they receive accounts + handlers as props. */

import { type ReactElement, type ReactNode } from "react";
import type { BrokerAccount } from "@/lib/brokerAccounts";
import { AccountRow } from "./AccountRow";

export function PageHeader({ onAddAccount }: Readonly<{ onAddAccount: () => void }>): ReactElement {
  return (
    <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="mb-1">Brokers &amp; accounts</h1>
        <p className="text-sm text-ink-secondary">
          Each strategy routes to a specific account. Daily login is per-account.
        </p>
      </div>
      <button type="button" className="btn btn-primary" onClick={onAddAccount}>
        <i className="ti ti-plus text-base" /> Add account
      </button>
    </header>
  );
}

interface BrokerCardProps {
  broker: "Zerodha" | "Fyers" | "Dhan";
  subtitle: string;
  icon: string;
  accounts: BrokerAccount[];
  now: Date;
  busy: boolean;
  onConnect: () => void;
  onDisconnect: (account: BrokerAccount) => void;
  /** Broker-specific extras under the account rows (Dhan: F&O status, renew). */
  footer?: ReactNode;
}

export function BrokerCard(props: Readonly<BrokerCardProps>): ReactElement {
  const { broker, accounts, now, busy, onConnect, onDisconnect } = props;
  return (
    <section className="card mb-3">
      <CardHeader broker={broker} subtitle={props.subtitle} icon={props.icon} accounts={accounts} />
      {accounts.length === 0 ? (
        <EmptyAccounts broker={broker} onConnect={onConnect} />
      ) : (
        <div className="flex flex-col gap-2">
          {accounts.map((a) => (
            <AccountRow
              key={a.id}
              account={a}
              now={now}
              busy={busy}
              onConnect={onConnect}
              onDisconnect={() => {
                onDisconnect(a);
              }}
            />
          ))}
        </div>
      )}
      {props.footer}
    </section>
  );
}

function CardHeader({
  broker,
  subtitle,
  icon,
  accounts,
}: Readonly<{
  broker: string;
  subtitle: string;
  icon: string;
  accounts: BrokerAccount[];
}>): ReactElement {
  const connectedCount = accounts.filter((a) => a.status === "connected").length;
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-bg">
          <i className={`ti ${icon} text-xl text-accent`} aria-hidden />
        </div>
        <div>
          <div className="text-base font-medium">{broker}</div>
          <div className="text-xs text-ink-secondary">
            {subtitle} · {String(accounts.length)} account
            {accounts.length === 1 ? "" : "s"} · {String(connectedCount)} connected
          </div>
        </div>
      </div>
      <span className="pill bg-success-bg text-success">
        <i className="ti ti-circle-check text-xs" /> Active broker
      </span>
    </div>
  );
}

function EmptyAccounts({
  broker,
  onConnect,
}: Readonly<{ broker: string; onConnect: () => void }>): ReactElement {
  return (
    <div className="rounded-xl border border-dashed border-line-strong bg-surface-1 p-6 text-center">
      <i className="ti ti-plug-off text-3xl text-ink-muted" aria-hidden />
      <p className="mt-2 text-sm text-ink-secondary">No accounts configured yet.</p>
      <button type="button" className="btn btn-primary mt-3" onClick={onConnect}>
        <i className="ti ti-link text-base" /> Connect {broker} account
      </button>
    </div>
  );
}

export function FuturePlaceholders(): ReactElement {
  return (
    <section className="card mb-3 opacity-70">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-2">
            <i className="ti ti-building-bank text-xl text-ink-muted" aria-hidden />
          </div>
          <div>
            <div className="text-base font-medium">Upstox</div>
            <div className="text-xs text-ink-muted">
              Additional brokers slot in here once each client is implemented.
            </div>
          </div>
        </div>
        <span className="pill bg-surface-2 text-ink-muted">Planned</span>
      </div>
    </section>
  );
}

export function StrategyMapping({
  accounts,
}: Readonly<{ accounts: BrokerAccount[] }>): ReactElement {
  const pairs: { strategy: string; account: BrokerAccount }[] = [];
  for (const a of accounts) {
    for (const s of a.assignedStrategies) pairs.push({ strategy: s, account: a });
  }
  return (
    <section className="card">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <i className="ti ti-target text-lg text-accent" />
          <h3>Strategy -&gt; account mapping</h3>
        </div>
        <span className="text-xs text-ink-muted">{String(pairs.length)} mapped</span>
      </div>
      {pairs.length === 0 ? (
        <p className="text-sm text-ink-secondary">
          No strategies created yet, or the broker is disconnected.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {pairs.map((p) => (
            <div
              key={p.strategy}
              className="flex items-center justify-between rounded-ctl bg-surface-2 px-3 py-2 text-sm"
            >
              <span>{p.strategy}</span>
              <span className="text-xs text-ink-secondary">
                <span className="font-mono text-ink">{p.account.userId ?? "—"}</span> ·{" "}
                {p.account.label} · {p.account.broker}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
